import { toValue, computed } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useStatementReservationsStore } from '@/stores/statements';
import { QueryGcTime } from '@/constants/http';

import api from './api';

export const useReservationById = (id, options = { gcTime: QueryGcTime }) => {
  const enabled = computed(() => !!id.value);
  return useQuery({
    queryKey: ['reservation', id],
    queryFn: () => api.getReservationById(toValue(id)),
    gcTime: options.gcTime ?? QueryGcTime,
    enabled,
  });
};

export const useCreateReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createReservation(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['statement', variables.statement_id] });
    },
  });
};

export const useUpdateReservation = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.updateReservation({ id, ...data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      queryClient.invalidateQueries({ queryKey: ['statement', variables.statement_id] });
    },
  });
};

export const useDeleteReservation = () => {
  const queryClient = useQueryClient();
  const { filters } = useStatementReservationsStore();

  return useMutation({
    mutationFn: (data) => api.deleteReservation(data.id),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['statement', variables.statement_id] });
      await queryClient.invalidateQueries({ queryKey: ['reservations', variables.statement_id] });

      const reservations = queryClient.getQueryData(['reservations', variables.statement_id, filters]);
      if (reservations?.data?.length === 0 && filters.page > 1) {
        filters.page = filters.page - 1;
      }
    },
  });
};

export const useGetEligibleStatements = (id, params = {}) => {
  return useQuery({
    queryKey: ['eligibleStatements', id, params],
    queryFn: () => api.getEligibleStatements(toValue(id), params),
    enabled: computed(() => !!id.value),
  });
};

export const useDeferReservation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.deferReservation(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reservations', variables.source_statement_id] });
      queryClient.invalidateQueries({ queryKey: ['statement', variables.source_statement_id] });
      queryClient.invalidateQueries({ queryKey: ['statement', variables.target_statement_id] });
    },
  });
};
